import { z } from "zod";
import type { ThreadSnapshot } from "./mail-content.ts";
import { SignOffModeSchema } from "./sign-off-mode.ts";

/** The subject a reply carries; the drafting host stores the same one with the draft. */
export { replySubject } from "../../../skills/leadcue-reply/scripts/lib/draft-subject.ts";

/*
 * The rep's own reply from the compose box (phase 1 of docs/2026-09-23-compose-view-shape.md), as
 * the page and the tests share it: its lifecycle read from the appended outcomes, the rule that
 * finds Bison's copy of it (R6), and the view the page is handed. Pure, so the browser imports it.
 */

/**
 * The run key a reply is bound to when its conversation has no run: imported history and replies
 * held with drafting off are answered too. `rep_replies.run_id` is the plan's `runId` otherwise.
 */
export const HELD_TURN = "held";

/** How far before the copy Bison's clock may put the rep's own message (R6). */
const COPY_LEEWAY_MS = 5 * 60_000;

/** One appended outcome of a reply, in recording order. `revision` is the text it was about. */
export type RepReplyOutcome = {
  kind: "copied" | "recorded" | "seen" | "withdrawn" | "discarded";
  revision: number | null;
  at: string;
};

/**
 * Where a reply stands. `copied` holds only while its copied text is still the newest: an edit
 * after copying means the pasted text is not what the box holds, so it must be copied again.
 * `confirming` and `holding` are page states and never reach here.
 */
export type RepReplyPhase =
  | { kind: "writing" }
  | { kind: "copied"; revision: number; copiedAt: string }
  | {
      kind: "reported";
      revision: number;
      copiedAt: string;
      reportedAt: string;
      seenAt: string | null;
    }
  | { kind: "discarded"; at: string };

export function repReplyPhase(
  outcomes: readonly RepReplyOutcome[],
  latestRevision: number,
): RepReplyPhase {
  let phase: RepReplyPhase = { kind: "writing" };
  let copied: { revision: number; at: string } | null = null;
  for (const outcome of outcomes) {
    switch (outcome.kind) {
      case "copied":
        copied = outcome.revision === null ? null : { revision: outcome.revision, at: outcome.at };
        if (phase.kind === "writing" || phase.kind === "copied")
          phase =
            copied && copied.revision === latestRevision
              ? { kind: "copied", revision: copied.revision, copiedAt: copied.at }
              : { kind: "writing" };
        break;
      case "recorded":
        phase = {
          kind: "reported",
          revision: copied?.revision ?? latestRevision,
          copiedAt: copied?.at ?? outcome.at,
          reportedAt: outcome.at,
          seenAt: null,
        };
        break;
      case "seen":
        phase = seenAt(phase, outcome.at);
        break;
      case "withdrawn":
        phase = { kind: "writing" };
        copied = null;
        break;
      case "discarded":
        phase = { kind: "discarded", at: outcome.at };
        break;
    }
  }
  // A revision saved after the copy voids it; the reported and closed phases do not move.
  if (phase.kind === "copied" && phase.revision !== latestRevision) return { kind: "writing" };
  return phase;
}

/** Bison's copy was seen: only a reported reply can be. */
function seenAt(phase: RepReplyPhase, at: string): RepReplyPhase {
  return phase.kind === "reported" ? { ...phase, seenAt: at } : phase;
}

/**
 * Bison's own copy of a reply the rep reported (R6): the earliest seller-side message after the
 * turn's message, and no earlier than the copy less five minutes, so an answer somebody sent before
 * the rep even copied theirs is never taken for it. The text is not compared: a rep who changed a
 * word while pasting still sent this reply, and the thread then shows Bison's words.
 */
export function localCopyMatch(
  thread: Pick<ThreadSnapshot, "messages">,
  turnMessageId: string,
  copiedAt: string,
): { messageId: string; at: string } | null {
  const turn = thread.messages.find((message) => message.id === turnMessageId);
  const after = turn ? Date.parse(turn.at) : Number.NEGATIVE_INFINITY;
  const floor = Date.parse(copiedAt) - COPY_LEEWAY_MS;
  const match = thread.messages
    .filter(
      (message) =>
        message.direction === "outbound" &&
        Date.parse(message.at) > after &&
        Date.parse(message.at) >= floor,
    )
    .sort((a, b) => Date.parse(a.at) - Date.parse(b.at))[0];
  return match ? { messageId: match.id, at: match.at } : null;
}

const EmailTextSchema = z.strictObject({ html: z.string(), plainText: z.string() });

/** The reply being written, as the box shows it. */
const RepReplyDraftSchema = z.strictObject({
  id: z.string(),
  turnMessageId: z.string(),
  /** False once the lead wrote again: the box says which message this answers. */
  turnCurrent: z.boolean(),
  phase: z.discriminatedUnion("kind", [
    z.strictObject({ kind: z.literal("writing") }),
    z.strictObject({ kind: z.literal("copied"), revision: z.number(), copiedAt: z.string() }),
  ]),
  /** 0 before the first save. */
  revision: z.number().int().nonnegative(),
  html: z.string(),
  plainText: z.string(),
  endingMode: SignOffModeSchema,
  ending: EmailTextSchema.nullable(),
  startedFromDraft: z.number().int().nonnegative().nullable(),
  savedAt: z.string().nullable(),
});
export type RepReplyDraft = z.infer<typeof RepReplyDraftSchema>;

/** The seller reply recorded against the current turn, whoever wrote it. */
const AnsweredTurnViewSchema = z.strictObject({
  repReplyId: z.string(),
  author: z.enum(["rep", "pipeline", "outside_app"]),
  at: z.string(),
  seenAt: z.string().nullable(),
  /** The steps this record closed, which the Work column labels "closed by your reply". */
  settledStepIds: z.array(z.string()),
  /** A rep's report, until Bison's copy is seen or the lead writes again (R11). */
  withdrawable: z.boolean(),
  /** A rep's own reply: its text, drawn in the thread until Bison's copy arrives (R6). */
  text: z
    .strictObject({ email: EmailTextSchema, copiedAt: z.string(), revision: z.number() })
    .nullable(),
  /** Whether the thread still needs the local copy: a rep's reply Bison's copy has not matched. */
  drawLocalCopy: z.boolean(),
});
export type AnsweredTurnView = z.infer<typeof AnsweredTurnViewSchema>;

export const RepReplyViewSchema = z.strictObject({
  /** The lead's message a reply here answers; null when the thread has none to answer. */
  turn: z.strictObject({ messageId: z.string(), at: z.string().nullable() }).nullable(),
  header: z.discriminatedUnion("kind", [
    z.strictObject({
      kind: z.literal("ready"),
      subject: z.string(),
      to: z.string(),
      from: z.string(),
      ccCount: z.number().int().nonnegative(),
    }),
    z.strictObject({ kind: z.literal("unresolvable") }),
  ]),
  /** The lead asked not to be contacted. Writing and copying stay allowed (D3). */
  suppressed: z.boolean(),
  /** The pipeline's draft is being sent, or may have been, by the app. */
  pipelineSend: z
    .strictObject({ state: z.enum(["in_flight", "uncertain"]), draftRevision: z.number() })
    .nullable(),
  /** Each ending the campaign mailbox can add, composed by the host; null adds nothing. */
  endings: z.strictObject({
    defaultMode: SignOffModeSchema,
    first_name: EmailTextSchema.nullable(),
    none: EmailTextSchema.nullable(),
    full_signature: EmailTextSchema.nullable(),
  }),
  reply: RepReplyDraftSchema.nullable(),
  answered: AnsweredTurnViewSchema.nullable(),
});
export type RepReplyView = z.infer<typeof RepReplyViewSchema>;

/** One line of a reply's history for the Activity tab, read from its own tables. */
export const RepReplyActivitySchema = z.strictObject({
  repReplyId: z.string(),
  kind: z.enum(["reported", "seen", "withdrawn", "discarded"]),
  at: z.string(),
  actorId: z.string(),
  /** The text as reported, for "Show this version". */
  email: EmailTextSchema.nullable(),
});
export type RepReplyActivity = z.infer<typeof RepReplyActivitySchema>;
